import React, { useEffect, useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Paper,
  Rating,
  Avatar,
  TextField,
  Button,
  Alert,
  Divider,
} from '@mui/material';
import api, { getMediaUrl } from '../services/api';

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [formData, setFormData] = useState({
    clientName: '',
    clientEmail: '',
    caseType: '',
    rating: 5,
    content: '',
  });
  const [alert, setAlert] = useState({ show: false, type: 'success', message: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await api.get('/testimonials');
        setTestimonials(response.data);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
      }
    };
    fetchTestimonials();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        clientName: formData.clientName,
        clientEmail: formData.clientEmail,
        caseType: formData.caseType,
        rating: Number(formData.rating),
        content: formData.content,
      };
      const res = await api.post('/testimonials', payload);
      // Admin has to approve; but we can optimistically show it in list if approved is default false
      setAlert({
        show: true,
        type: 'success',
        message: 'Thank you! Your testimonial has been submitted and will appear once approved.',
      });
      setFormData({
        clientName: '',
        clientEmail: '',
        caseType: '',
        rating: 5,
        content: '',
      });
    } catch (error) {
      setAlert({
        show: true,
        type: 'error',
        message: 'There was a problem submitting your testimonial. Please try again later.',
      });
      console.error('Error submitting testimonial:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container sx={{ py: 8 }}>
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h3" gutterBottom>
          Client Testimonials
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
          What our clients say about their experience with Advocate Priyanka Swami
        </Typography>
      </Box>

      {/* Existing testimonials list */}
      <Grid container spacing={4} sx={{ mb: 6 }}>
        {testimonials.length > 0 ? (
          testimonials.map((testimonial) => (
            <Grid item xs={12} md={6} key={testimonial._id}>
              <Paper
                sx={{
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  borderRadius: 3,
                  boxShadow: 3,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <Avatar
                    src={
                      testimonial.clientPhoto
                        ? getMediaUrl(testimonial.clientPhoto)
                        : undefined
                    }
                    sx={{ width: 56, height: 56, mr: 2, bgcolor: 'primary.main' }}
                  >
                    {testimonial.clientName?.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {testimonial.clientName}
                    </Typography>
                    {testimonial.caseType && (
                      <Typography variant="caption" color="text.secondary">
                        {testimonial.caseType}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Rating value={testimonial.rating} readOnly sx={{ mb: 1 }} />
                <Typography
                  variant="body2"
                  sx={{ fontStyle: 'italic', color: 'text.primary', mt: 1, flexGrow: 1 }}
                >
                  “{testimonial.content}”
                </Typography>
              </Paper>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Typography variant="body1" align="center" color="text.secondary">
              No testimonials available yet. Please check back soon.
            </Typography>
          </Grid>
        )}
      </Grid>

      <Divider sx={{ mb: 6 }} />

      {/* Submit testimonial form */}
      <Grid container spacing={4} sx={{ mb: 6 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 4, height: '100%' }}>
            <Typography variant="h5" gutterBottom>
              Share Your Experience
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              If you have worked with Advocate Priyanka Swami, you can share your feedback here.
              Your testimonial may be published on this page after review.
            </Typography>
            {alert.show && (
              <Alert
                severity={alert.type}
                sx={{ mb: 2 }}
                onClose={() => setAlert((prev) => ({ ...prev, show: false }))}
              >
                {alert.message}
              </Alert>
            )}
            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                label="Full Name"
                name="clientName"
                fullWidth
                margin="normal"
                required
                value={formData.clientName}
                onChange={handleChange}
              />
              <TextField
                label="Email (optional, not shown publicly)"
                name="clientEmail"
                type="email"
                fullWidth
                margin="normal"
                value={formData.clientEmail}
                onChange={handleChange}
              />
              <TextField
                label="Matter Type (e.g. Environmental, Civil, Criminal)"
                name="caseType"
                fullWidth
                margin="normal"
                value={formData.caseType}
                onChange={handleChange}
              />
              <Box sx={{ mt: 2, mb: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Rating
                </Typography>
                <Rating
                  name="rating"
                  value={Number(formData.rating)}
                  onChange={(_, value) =>
                    setFormData((prev) => ({ ...prev, rating: value || 0 }))
                  }
                />
              </Box>
              <TextField
                label="Your Testimonial"
                name="content"
                fullWidth
                margin="normal"
                multiline
                rows={4}
                required
                value={formData.content}
                onChange={handleChange}
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                sx={{ mt: 2 }}
                disabled={submitting}
                fullWidth
              >
                {submitting ? 'Submitting...' : 'Submit Testimonial'}
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            How Testimonials Are Used
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Testimonials help other clients understand the way Advocate Priyanka Swami approaches
            matters before the National Green Tribunal, Supreme Court and other forums. Only your
            name, rating and testimonial text are shown publicly; contact details are kept
            confidential and are used only if we need to verify feedback.
          </Typography>
        </Grid>
      </Grid>

      
    </Container>
  );
};

export default Testimonials;

